import React, { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { Link } from 'react-router-dom';
import './Messages.css';
import TopNavigation from './TopNavigation';
import BottomNavigation from './BottomNavigation';
import { useLocation } from 'react-router-dom';
import { Icon } from './Icon.js';
import SearchBar from './SearchBar';
import ReportModal from './ReportModal';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import JellyfishLoader from './JellyfishLoader';
import Picker from '@emoji-mart/react';
import ProtocolSelector from './ProtocolSelector';
import ProtocolShield from './ProtocolShield';
import BiohackInfoCard from './BiohackInfoCard';

/* ========================================
   UTILITY FUNCTIONS
   ======================================== */

const formatTime = (date) => {
  if (!date) return '';
  const messageDate = new Date(date);
  return messageDate.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
};

const formatLastSeen = (lastSeen) => {
  if (!lastSeen) return 'Offline';
  
  const now = new Date();
  const seenDate = new Date(lastSeen);
  const diffMs = now - seenDate;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Active now';
  if (diffMins < 5) return 'Active recently';
  if (diffMins < 60) return `Active ${diffMins}m ago`;
  if (diffHours < 24) return `Active ${diffHours}h ago`;
  if (diffDays === 1) return 'Active yesterday';
  if (diffDays < 7) return `Active ${diffDays}d ago`;
  
  return `Active ${seenDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

const formatConversationTime = (date) => {
  if (!date) return '';
  const messageDate = new Date(date);
  const now = new Date();
  const isToday = messageDate.toDateString() === now.toDateString();
  const isYesterday = messageDate.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
  
  if (isToday) {
    return messageDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  } else if (isYesterday) {
    return 'Yesterday';
  } else {
    return messageDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

const formatDateSeparator = (date) => {
  if (!date) return '';
  const messageDate = new Date(date);
  const now = new Date();
  
  const isToday = messageDate.toDateString() === now.toDateString();
  const isYesterday = messageDate.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString();
  
  if (isToday) return 'Today';
  if (isYesterday) return 'Yesterday';

    return messageDate.toLocaleDateString('en-US', { 
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
};

const groupMessagesByDate = (messages) => {
  if (!messages || messages.length === 0) return [];
  
  const groups = [];
  let currentGroup = null;
  
  messages.forEach(message => {
    const messageDate = new Date(message.sent_at);
    const dateKey = messageDate.toDateString();
    
    if (!currentGroup || currentGroup.dateKey !== dateKey) {
      currentGroup = {
        dateKey,
        date: messageDate,
        messages: []
      };
      groups.push(currentGroup);
    }
    
    currentGroup.messages.push(message);
  });
  
  return groups;
};

/* ========================================
   SIDEBAR COMPONENT
   ======================================== */

const MessagesSidebar = ({ search, onSearchChange, conversations, selectedId, onSelectConversation, loading }) => {
  return (
    <aside className="messages-sidebar">
      <header className="messages-sidebar-header">
        <h1>Messages</h1>
      </header>

      <div className="messages-sidebar-search">
        <SearchBar
          value={search}
          onChange={onSearchChange}
          placeholder="Search messages or contacts..."
        />
      </div>

      <div className="messages-sidebar-list">
        {loading ? (
          <div className="messages-sidebar-loading">
            <JellyfishLoader />
            <p>Loading your conversations...</p>
          </div>
        ) : conversations.length === 0 ? (
          <div className="messages-sidebar-empty">
            {search ? (
              <>
                <Icon name="Search" className="icon--lg empty-icon" />
                <p>No results found</p>
              </>
            ) : (
              <>
                <Icon name="MessageCircle" className="icon--lg empty-icon" />
                <h3>No messages yet</h3>
                <p>Search for contacts to start messaging</p>
              </>
            )}
          </div>
        ) : (
          conversations.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => {
                onSelectConversation(conversation.id);
                if (conversation.isNewContact) {
                  onSearchChange('');
                }
              }}
              className={`conversation-item ${selectedId === conversation.id ? 'active' : ''} ${conversation.isTemporary ? 'temporary' : ''} ${conversation.isNewContact ? 'new-contact' : ''}`}
              aria-label={`Chat with ${conversation.username}`}
            >
              <div className="conversation-avatar">
                {conversation.avatar ? (
                  <img src={conversation.avatar} alt={conversation.username} />
                ) : (
                  <span className="avatar-fallback">{conversation.username.charAt(0).toUpperCase()}</span>
                )}
              </div>

              <div className="conversation-content">
                <div className="conversation-header">
                  <span className="conversation-name">
                    {conversation.username}
                    {conversation.isNewContact && <span className="new-contact-badge">New</span>}
                  </span>
                  {!conversation.isNewContact && (
                    <span className="conversation-time">{formatConversationTime(conversation.lastMessageAt)}</span>
                  )}
                </div>
                <p className={`conversation-message ${conversation.isTemporary ? 'temporary-message' : ''}`}>
                  {conversation.lastMessage || 'No messages yet'}
                </p>
              </div>
            </button>
          ))
        )}
      </div>
    </aside>
  );
};

/* ========================================
   CHAT THREAD COMPONENT
   ======================================== */

const MessagesChatThread = ({
  selectedProfile, thread, loadingThread, user, userProfiles, message, onMessageChange,
  onSendMessage, replyToMessage, onSetReplyToMessage, showEmojiPicker, onToggleEmojiPicker,
  onInsertEmoji, inputRef, onBack, isMobile, onReportMessage, messageReactions, onReaction,
  editingMessage, onSetEditingMessage, onEditMessage, userPresence, selectedProtocol, onSelectProtocol,
  onOpenProtocolSelector
}) => {
  const threadRef = useRef(null);
  const [showReactionPicker, setShowReactionPicker] = useState(null);
  const [emojiHoverTimeout, setEmojiHoverTimeout] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  const [favourites, setFavourites] = useState([]);
  const [ratedBiohackIds, setRatedBiohackIds] = useState(new Set());

  // Fetch current user's favourites for BiohackInfoCard
  useEffect(() => {
    const fetchFavourites = async () => {
      if (!user?.id) return;
      try {
        const cacheKey = getCacheKey('fetchFavourites', user.id);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: favData, error } = await supabase
            .from('hack_favourited')
            .select('hack_id')
            .eq('user_id', user.id);
          if (error) throw error;
          return favData || [];
        });
        setFavourites(data || []);
      } catch (e) {
        console.error('Error fetching favourites in Messages:', e);
      }
    };
    fetchFavourites();
  }, [user?.id]);

  // Fetch user's rated biohack IDs for BiohackInfoCard star state
  useEffect(() => {
    const fetchUserRatings = async () => {
      if (!user?.id) return;
      try {
        const cacheKey = getCacheKey('fetchUserRatings', user.id);
        const data = await dedupeRequest(cacheKey, async () => {
          const { data: ratingData, error } = await supabase
            .from('hack_scoring')
            .select('hack_id')
            .eq('user_id', user.id);
          if (error) throw error;
          return ratingData || [];
        });
        setRatedBiohackIds(new Set((data || []).map(r => r.hack_id)));
      } catch (e) {
        console.error('Error fetching user ratings in Messages:', e);
      }
    };
    fetchUserRatings();
  }, [user?.id]);

  const handleToggleFavourite = useCallback(async (hackId) => {
    if (!user?.id) return;
    try {
      const isFavourited = favourites.some(fav => fav.hack_id === hackId);
      if (isFavourited) {
        const { error } = await supabase
          .from('hack_favourited')
          .delete()
          .eq('user_id', user.id)
          .eq('hack_id', hackId);
        if (error) throw error;
        setFavourites(prev => prev.filter(fav => fav.hack_id !== hackId));
      } else {
        const { error } = await supabase
          .from('hack_favourited')
          .insert({
            user_id: user.id,
            hack_id: hackId,
            timestamp: new Date().toISOString()
          });
        if (error) throw error;
        setFavourites(prev => [...prev, { hack_id: hackId }]);
      }
    } catch (e) {
      console.error('Error toggling favourite in Messages:', e);
    }
  }, [user?.id, favourites]);

  // Handle emoji picker hover
  const handleEmojiButtonMouseEnter = () => {
    if (emojiHoverTimeout) {
      clearTimeout(emojiHoverTimeout);
      setEmojiHoverTimeout(null);
    }
    if (!showEmojiPicker) {
      onToggleEmojiPicker();
    }
  };

  const handleEmojiButtonMouseLeave = () => {
    const timeout = setTimeout(() => {
      if (showEmojiPicker) {
        onToggleEmojiPicker();
      }
    }, 200); // Small delay to allow moving to the emoji picker
    setEmojiHoverTimeout(timeout);
  };

  const handleEmojiPickerMouseEnter = () => {
    if (emojiHoverTimeout) {
      clearTimeout(emojiHoverTimeout);
      setEmojiHoverTimeout(null);
    }
  };

  const handleEmojiPickerMouseLeave = () => {
    const timeout = setTimeout(() => {
      if (showEmojiPicker) {
        onToggleEmojiPicker();
      }
    }, 200);
    setEmojiHoverTimeout(timeout);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (emojiHoverTimeout) {
        clearTimeout(emojiHoverTimeout);
      }
    };
  }, [emojiHoverTimeout]);

  // Scroll to bottom when thread loads or changes
  useEffect(() => {
    if (!loadingThread && threadRef.current && thread.length > 0) {
      requestAnimationFrame(() => {
        if (threadRef.current) {
          threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
      });
    }
  }, [thread, loadingThread]);

  const getMessageById = (id) => thread.find(m => m.id === id);

  const isEmojiOnly = (text) => {
    // Remove all emojis and check if anything is left
    const emojiRegex = /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu;
    const withoutEmojis = text.replace(emojiRegex, '').trim();
    return withoutEmojis.length === 0 && text.trim().length > 0;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const handleEditKeyPress = (e, msgId) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit(msgId);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleStartEdit = (msg) => {
    onSetEditingMessage(msg);
    setEditedContent(msg.content);
  };

  const handleCancelEdit = () => {
    onSetEditingMessage(null);
    setEditedContent('');
  };

  const handleSaveEdit = (msgId) => {
    if (editedContent.trim() && editedContent !== editingMessage?.content) {
      onEditMessage(msgId, editedContent.trim());
    }
    handleCancelEdit();
  };

  if (!selectedProfile) {
    return (
      <div className="chat-thread">
        <div className="chat-empty-state">
          <Icon name="MessageCircle" className="icon--xxl empty-icon" />
          <h3>Select a conversation</h3>
          <p>Choose a conversation from the sidebar to start messaging</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-thread">
      <header className="chat-header">
        {isMobile && (
          <button onClick={onBack} className="btn-back" aria-label="Back to conversations">
            <Icon name="ArrowLeft" className="icon--sm" />
          </button>
        )}

        <div className="chat-user-info">
          <div className="chat-avatar">
            {selectedProfile.avatar ? (
              <img src={selectedProfile.avatar} alt={selectedProfile.username} />
            ) : (
              <span className="avatar-fallback">{(selectedProfile.username || 'U').charAt(0).toUpperCase()}</span>
            )}
          </div>
          <div className="chat-user-details">
            <h2 className="chat-username">
              {selectedProfile.username ? (
                <Link to={`/user/${selectedProfile.id}`}>{selectedProfile.username}</Link>
              ) : (
                'Unknown User'
              )}
            </h2>
            <span className={`chat-status ${userPresence?.is_online ? 'online' : 'offline'}`}>
              {userPresence?.is_online ? 'Active now' : formatLastSeen(userPresence?.last_seen)}
            </span>
          </div>
        </div>

        <div className="chat-actions">
          <button className="btn-chat-action btn-chat-action--inactive" title="Voice call" aria-label="Voice call">
            <Icon name="Phone" className="icon--sm" />
          </button>
          <button className="btn-chat-action btn-chat-action--inactive" title="Video call" aria-label="Video call">
            <Icon name="Video" className="icon--sm" />
          </button>
          <button className="btn-chat-action btn-chat-action--inactive" title="More options" aria-label="More options">
            <Icon name="MoreVertical" className="icon--sm" />
          </button>
        </div>
      </header>

      <div className="chat-messages-area" ref={threadRef}>
        {loadingThread ? (
          <div className="chat-loading">
            <JellyfishLoader />
            <p>Loading messages...</p>
          </div>
        ) : thread.length === 0 ? (
          <div className="chat-empty-messages">
            <Icon name="MessageCircle" className="icon--xxl empty-icon" />
            <h3>No messages yet</h3>
            <p>Send a message to start the conversation</p>
          </div>
        ) : (
          <div className="messages-list">
            {groupMessagesByDate(thread).map((group) => (
              <div key={group.dateKey} className="message-date-group">
                <div className="date-separator">
                  <span className="date-separator-text">{formatDateSeparator(group.date)}</span>
                </div>

                {group.messages.map((msg) => {
                  const replyMsg = msg.reply_to_message_id ? getMessageById(msg.reply_to_message_id) : null;
                  const isOwnMessage = msg.sender_id === user.id;
                  const senderProfile = userProfiles[msg.sender_id];

                  const isEmoji = isEmojiOnly(msg.content);
                  const reactions = messageReactions[msg.id] || [];
                  const groupedReactions = reactions.reduce((acc, r) => {
                    if (!acc[r.emoji]) {
                      acc[r.emoji] = { count: 0, users: [], hasUserReacted: false };
                    }
                    acc[r.emoji].count++;
                    acc[r.emoji].users.push(r.user_id);
                    if (r.user_id === user.id) {
                      acc[r.emoji].hasUserReacted = true;
                    }
                    return acc;
                  }, {});

                  return (
                    <div key={msg.id} className={`message-item ${isOwnMessage ? 'own' : 'other'} ${isEmoji ? 'emoji-only' : ''}`}>
                      {!isOwnMessage && !isEmoji && (
                        <div className="message-avatar">
                          {senderProfile?.profile_picture_url ? (
                            <img src={senderProfile.profile_picture_url} alt={senderProfile.username || 'User'} />
                          ) : (
                            <span className="avatar-fallback">
                              {(senderProfile?.username || 'U').charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                      )}

                      {isEmoji ? (
                        <div className="emoji-message">
                          <span className="emoji-content">{msg.content}</span>
                          <span className="emoji-time">{formatTime(msg.sent_at)}</span>
                        </div>
                      ) : (
                        <div className="message-bubble-container">
                          <div className="message-bubble">
                            {replyMsg && (
                              <div className="message-reply-preview">
                                <div className="reply-line" />
                                <div className="reply-content">
                                  <span className="reply-author">
                                    {replyMsg.sender_id === user.id ? 'You' : selectedProfile?.username}
                                  </span>
                                  <span className="reply-text">
                                    {replyMsg.content.slice(0, 60)}{replyMsg.content.length > 60 ? '...' : ''}
                                  </span>
                                </div>
                              </div>
                            )}

                            {editingMessage?.id === msg.id ? (
                              <div className="message-edit-container">
                                <textarea
                                  value={editedContent}
                                  onChange={(e) => setEditedContent(e.target.value)}
                                  onKeyDown={(e) => handleEditKeyPress(e, msg.id)}
                                  className="message-edit-input"
                                  autoFocus
                                  maxLength={500}
                                />
                                <div className="message-edit-actions">
                                  <button onClick={handleCancelEdit} className="btn-edit-cancel">
                                    Cancel
                                  </button>
                                  <button 
                                    onClick={() => handleSaveEdit(msg.id)} 
                                    className="btn-edit-save"
                                    disabled={!editedContent.trim() || editedContent === msg.content}
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                {msg.content && <div className="message-text">{msg.content}</div>}
                                
                                {msg.attachedProtocols && msg.attachedProtocols.length > 0 && (
                                  <div className="message-protocols">
                                    {msg.attachedProtocols.map(({ protocol, protocol_id, visible }) => (
                                      <div key={protocol_id} className="message-protocol-item">
                                        {visible && protocol ? (
                                          <div className="message-protocol-card">
                                            <ProtocolShield
                                              protocol={protocol}
                                              size="small"
                                              disableFlip={true}
                                              hideFabMenu={true}
                                              onBiohackClick={(biohack) => setSelectedBiohack(biohack)}
                                            />
                                          </div>
                                        ) : (
                                          <div className="message-protocol-placeholder" aria-hidden="true">
                                            Protocol not visible due to original poster&apos;s privacy settings.
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                <div className="message-meta">
                                  <span className="message-time">{formatTime(msg.sent_at)}</span>
                                  {msg.edited_at && <span className="message-edited">(edited)</span>}
                                  {isOwnMessage && (
                                    <div className="message-status">
                                      {msg.read_at ? (
                                        <div className="message-check-double">
                                          <Icon name="Check" className="icon--xs check-icon" />
                                          <Icon name="Check" className="icon--xs check-icon check-icon-second" />
                                        </div>
                                      ) : (
                                        <Icon name="Check" className="icon--xs message-check" />
                                      )}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}

                            {!isOwnMessage && !editingMessage && (
                              <div className="message-actions">
                                <button
                                  onClick={() => setShowReactionPicker(showReactionPicker === msg.id ? null : msg.id)}
                                  className="btn-message-action"
                                  title="React"
                                  aria-label="Add reaction"
                                >
                                  <Icon name="Smile" className="icon--xs" />
                                </button>
                                <button onClick={() => onSetReplyToMessage(msg)} className="btn-message-action" title="Reply" aria-label="Reply">
                                  <Icon name="Reply" className="icon--xs" />
                                </button>
                                <button onClick={() => onReportMessage(msg)} className="btn-message-action" title="Report" aria-label="Report">
                                  <Icon name="AlertTriangle" className="icon--xs" />
                                </button>
                              </div>
                            )}

                            {isOwnMessage && !editingMessage && (
                              <div className="message-actions">
                                <button onClick={() => handleStartEdit(msg)} className="btn-message-action" title="Edit" aria-label="Edit message">
                                  <Icon name="Edit" className="icon--xs" />
                                </button>
                                <button onClick={() => onSetReplyToMessage(msg)} className="btn-message-action" title="Reply" aria-label="Reply">
                                  <Icon name="Reply" className="icon--xs" />
                                </button>
                              </div>
                            )}

                            {showReactionPicker === msg.id && (
                              <div className="quick-reactions">
                                {['👍', '❤️', '😂', '😮', '😢', '🙏'].map(emoji => (
                                  <button
                                    key={emoji}
                                    onClick={() => {
                                      onReaction(msg.id, emoji);
                                      setShowReactionPicker(null);
                                    }}
                                    className="quick-reaction-btn"
                                  >
                                    {emoji}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {Object.keys(groupedReactions).length > 0 && (
                            <div className="message-reactions">
                              {Object.entries(groupedReactions).map(([emoji, data]) => (
                                <button
                                  key={emoji}
                                  onClick={() => onReaction(msg.id, emoji)}
                                  className={`reaction-bubble ${data.hasUserReacted ? 'user-reacted' : ''}`}
                                >
                                  <span className="reaction-emoji">{emoji}</span>
                                  {data.count > 1 && <span className="reaction-count">{data.count}</span>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {replyToMessage && (
        <div className="chat-reply-bar">
          <div className="reply-bar-content">
            <div className="reply-line" />
            <div className="reply-info">
              <span className="reply-label">
                Replying to {replyToMessage.sender_id === user.id ? 'yourself' : selectedProfile?.username}
              </span>
              <span className="reply-text">{replyToMessage.content.slice(0, 60)}</span>
            </div>
          </div>
          <button onClick={() => onSetReplyToMessage(null)} className="btn-cancel-reply" aria-label="Cancel reply">
            <Icon name="X" className="icon--sm" />
          </button>
        </div>
      )}

      {selectedProtocol && (
        <div className="chat-protocol-preview">
          <div className="protocol-preview-content">
            <div className="protocol-preview-shield">
              <ProtocolShield
                protocol={selectedProtocol}
                size="small"
                disableFlip={true}
                hideFabMenu={true}
              />
            </div>
          </div>
          <button onClick={() => onSelectProtocol(null)} className="btn-cancel-protocol" aria-label="Remove protocol">
            <Icon name="X" className="icon--sm" />
          </button>
        </div>
      )}

      {selectedBiohack && (
        <BiohackInfoCard
          biohack={selectedBiohack}
          onClose={() => setSelectedBiohack(null)}
          isFavourited={favourites.some(fav => fav.hack_id === selectedBiohack.id)}
          onToggleFavourite={() => handleToggleFavourite(selectedBiohack.id)}
          hasRated={ratedBiohackIds.has(selectedBiohack.id)}
          onRatingSubmitted={() => setRatedBiohackIds(prev => new Set([...prev, selectedBiohack.id]))}
        />
      )}

      <div className="chat-input-container">
        <div className="chat-input-wrapper">
          <button 
            onClick={onOpenProtocolSelector}
            className="btn-input-action" 
            title="Attach protocol" 
            aria-label="Attach protocol"
          >
            <Icon name="TrendingUp" className="icon--sm" />
          </button>

          <button 
            onMouseEnter={handleEmojiButtonMouseEnter}
            onMouseLeave={handleEmojiButtonMouseLeave}
            className="btn-input-action" 
            title="Add emoji" 
            aria-label="Add emoji"
          >
            <Icon name="Smile" className="icon--sm" />
          </button>

          <textarea
            ref={inputRef}
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="chat-input"
            rows="1"
            maxLength={500}
            aria-label="Message input"
          />

          <button onClick={onSendMessage} disabled={!message.trim() && !selectedProtocol} className="btn-send" title="Send" aria-label="Send">
            <Icon name="Send" className="icon--sm" />
          </button>
        </div>

        {showEmojiPicker && (
          <div 
            className="emoji-picker-popup"
            onMouseEnter={handleEmojiPickerMouseEnter}
            onMouseLeave={handleEmojiPickerMouseLeave}
          >
            <Picker onEmojiSelect={onInsertEmoji} theme="light" previewPosition="none" skinTonePosition="none" />
          </div>
        )}
      </div>
    </div>
  );
};

/* ========================================
   MAIN MESSAGES COMPONENT
   ======================================== */

const Messages = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [thread, setThread] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const inputRef = useRef(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedMessageForReport, setSelectedMessageForReport] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [messageReactions, setMessageReactions] = useState({});
  const [editingMessage, setEditingMessage] = useState(null);
  const [userPresence, setUserPresence] = useState(null);
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [showProtocolSelector, setShowProtocolSelector] = useState(false);
  
  // Disclaimer modal state
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    
      setLoading(true);
    const { data: messages, error } = await supabase
        .from('messages')
        .select('*')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order('sent_at', { ascending: false });

      if (error) {
        setConversations([]);
        setLoading(false);
        return;
      }

      const convMap = {};
      messages.forEach(msg => {
        const otherId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        if (otherId === user.id) return;
        const key = [user.id, otherId].sort().join('-');
        if (!convMap[key]) convMap[key] = msg;
      });

      const convs = Object.values(convMap);
      const userIds = convs.map(msg => (msg.sender_id === user.id ? msg.receiver_id : msg.sender_id));

      // Use deduplication to prevent duplicate requests
      let profiles = [];
      if (userIds.length > 0) {
        const profilesCacheKey = getCacheKey('fetchConversationUserProfiles', userIds.sort().join(','));
        profiles = await dedupeRequest(profilesCacheKey, async () => {
          const { data, error } = await supabase
            .from('user_profiles')
            .select('user_id,profile_picture_url,username')
            .in('user_id', userIds);
          if (error) throw error;
          return data;
        }) || [];
      }

      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.user_id] = p; });

      const merged = convs.map(msg => {
        const otherId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
        const profile = profileMap[otherId] || {};
        return {
          id: otherId,
          username: profile.username || 'Unknown',
          avatar: profile.profile_picture_url,
          lastMessage: msg.content,
          lastMessageAt: msg.sent_at,
          messageId: msg.id,
        };
      });

      setConversations(merged);
      setLoading(false);
  }, [user]);

  const fetchThread = useCallback(async () => {
    if (!selected || !user) return;

      setLoadingThread(true);
    const { data: messages, error } = await supabase
      .from('messages')
      .select(`
        *,
        message_attachments(
          id,
          type,
          source_post_id,
          message_attachment_protocols(
            id,
            protocol_id,
            protocols(
              id,
              name,
              description,
              created_by,
              created_at,
              trending_rank,
              adherence_score,
              pop_score,
              user_profiles!protocols_created_by_fkey(
                user_id,
                username
              ),
              protocol_hacks(
                hack_id,
                position,
                biohacks(
                  id,
                  name,
                  image_url,
                  category
                )
              )
            )
          )
        )
      `)
      .or(`sender_id.eq.${user.id},sender_id.eq.${selected}`)
      .or(`receiver_id.eq.${user.id},receiver_id.eq.${selected}`)
      .order('sent_at', { ascending: true });

      if (error) {
        setThread([]);
        setLoadingThread(false);
        return;
      }

      const filtered = messages.filter(
        m => (m.sender_id === user.id && m.receiver_id === selected) ||
             (m.sender_id === selected && m.receiver_id === user.id)
      );

      // Collect source_post_ids for protocol attachments (only way to share protocol in message is from a post)
      const sourcePostIds = [];
      filtered.forEach(msg => {
        if (!msg.message_attachments?.length) return;
        msg.message_attachments
          .filter(att => att.type === 'protocols')
          .forEach(att => {
            if (att.source_post_id) sourcePostIds.push(att.source_post_id);
          });
      });
      const uniquePostIds = [...new Set(sourcePostIds)];

      // Fetch source posts (visibility, user_id) - visibility is ONLY based on post, never tab privacy
      let postsMap = {};
      if (uniquePostIds.length > 0) {
        const { data: postsData } = await supabase
          .from('posts')
          .select('id, visibility, user_id')
          .in('id', uniquePostIds);
        if (postsData) {
          postsData.forEach(p => { postsMap[p.id] = p; });
        }
      }

      // Follows: receiver following post authors (for followers-only posts)
      const postAuthorIds = [...new Set(Object.values(postsMap).map(p => p.user_id).filter(Boolean))];
      let followeeIds = new Set();
      if (postAuthorIds.length > 0) {
        const { data: followsData } = await supabase
          .from('follows')
          .select('followee_id')
          .eq('follower_id', user.id)
          .in('followee_id', postAuthorIds)
          .not('accepted_at', 'is', null);
        if (followsData) followsData.forEach(f => followeeIds.add(f.followee_id));
      }

      // Receiver can see protocol only if the original post is public, or followers-only and they follow the poster. No tab privacy.
      function canViewProtocolFromPost(sourcePost) {
        if (!sourcePost) return false;
        if (sourcePost.visibility === 'public') return true;
        if (sourcePost.visibility === 'followers') return followeeIds.has(sourcePost.user_id);
        return false;
      }

      // Process protocol attachments: from post = use post visibility; from messages bar = always visible to receiver
      const processedMessages = filtered.map(msg => {
        const isOwnMessage = msg.sender_id === user.id;
        const viewerIsReceiver = msg.receiver_id === user.id;

        if (msg.message_attachments && msg.message_attachments.length > 0) {
          const protocolItems = msg.message_attachments
            .filter(att => att.type === 'protocols')
            .flatMap(att => {
              const sourcePost = att.source_post_id ? postsMap[att.source_post_id] : null;
              const sentFromMessagesPage = !att.source_post_id;
              const visible = isOwnMessage
                || (viewerIsReceiver && (sentFromMessagesPage || canViewProtocolFromPost(sourcePost)));
              return (att.message_attachment_protocols || [])
                .map(map => ({ protocol: map.protocols, protocol_id: map.protocol_id }))
                .filter(({ protocol_id }) => protocol_id)
                .map(({ protocol, protocol_id }) => ({ protocol, protocol_id, visible }));
            });

          return {
            ...msg,
            attachedProtocols: protocolItems
          };
        }
        return { ...msg, attachedProtocols: [] };
      });

      setThread(processedMessages);
      
      // Mark messages from the other user as read
      await markMessagesAsRead(user.id, selected);
      
      const userIds = [user.id, selected];
      const profilesCacheKey = getCacheKey('fetchMessageUserProfiles', userIds.sort().join(','));
      const profiles = await dedupeRequest(profilesCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id,profile_picture_url,username')
          .in('user_id', userIds);
        if (error) throw error;
        return data;
      });
      
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.user_id] = p; });
    setUserProfiles(profileMap);

    // Fetch reactions for messages (optional - table may not exist)
    if (filtered.length > 0) {
      try {
        const messageIds = filtered.map(m => m.id);
        const { data: reactions, error: reactionsError } = await supabase
          .from('message_reactions')
          .select('*')
          .in('message_id', messageIds);

        if (reactionsError) {
          setMessageReactions({});
        } else {
          const reactionsMap = {};
          (reactions || []).forEach(r => {
            if (!reactionsMap[r.message_id]) {
              reactionsMap[r.message_id] = [];
            }
            reactionsMap[r.message_id].push(r);
          });
          setMessageReactions(reactionsMap);
        }
      } catch (error) {
        setMessageReactions({});
      }
    }
      
      setLoadingThread(false);
  }, [selected, user]);

  useEffect(() => {
    const fetchUser = async () => {
      // Use getSession() to avoid network requests (uses cached session data)
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      setUser(user);
      
      // Update user's presence to online when component mounts (optional - function may not exist)
      if (user) {
        try {
          await supabase.rpc('update_user_presence', { p_is_online: true });
        } catch (error) {
          // Silently handle presence update errors
        }
      }
    };
    fetchUser();
    
    // Mark user as offline when leaving page (optional - function may not exist)
    return () => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          const rpcCall = supabase.rpc('mark_user_offline');
          if (rpcCall && typeof rpcCall.then === 'function') {
            rpcCall.catch(() => {
              // Silently handle offline marking errors
            });
          }
        }
      }).catch(() => {
        // Silently handle session retrieval errors
      });
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    const markAsRead = async () => {
      await supabase.from('notifications').update({ is_read: true }).eq('user_id', user.id).eq('type', 'message');
    };
    markAsRead();
  }, [user]);

  useEffect(() => { fetchConversations(); }, [fetchConversations]);
  useEffect(() => { fetchThread(); }, [fetchThread]);

  // Fetch all users for unified search
  useEffect(() => {
    if (!user) return;

    const fetchAllUsers = async () => {
      // Use deduplication to prevent duplicate requests
      const allUsersCacheKey = getCacheKey('fetchAllUserProfiles', 'all');
      const profiles = await dedupeRequest(allUsersCacheKey, async () => {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('user_id,username,profile_picture_url');
        if (error) throw error;
        return data;
      });
      setAllUsers((profiles || []).filter(p => p.user_id !== user.id));
    };

      fetchAllUsers();
  }, [user]);

  useEffect(() => {
    if (location.state?.newMessageUserId) {
      setSelected(location.state.newMessageUserId);
    }
  }, [location.state]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch and subscribe to selected user's presence
  useEffect(() => {
    if (!selected) {
      setUserPresence(null);
      return;
    }

    // Fetch initial presence (optional - table may not exist)
    const fetchPresence = async () => {
      try {
        const { data, error: presenceError } = await supabase
          .from('user_presence')
          .select('*')
          .eq('user_id', selected)
          .maybeSingle();
        
        if (presenceError) {
          setUserPresence(null);
        } else {
          // maybeSingle() returns null if no rows found, which is fine
          setUserPresence(data);
        }
      } catch (error) {
        setUserPresence(null);
      }
    };

    fetchPresence();

    // Subscribe to presence changes (optional - table may not exist)
    let presenceChannel = null;
    try {
      presenceChannel = supabase
        .channel(`presence:${selected}`)
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'user_presence',
          filter: `user_id=eq.${selected}`
        }, (payload) => {
          setUserPresence(payload.new);
        })
        .subscribe();
    } catch (error) {
      // Silently handle subscription errors
    }

    return () => {
      if (presenceChannel) {
        supabase.removeChannel(presenceChannel);
      }
    };
  }, [selected]);

  // Keep own presence updated while on messages page
  useEffect(() => {
    if (!user) return;

    // Update presence every 2 minutes (optional - function may not exist)
    const presenceInterval = setInterval(async () => {
      try {
        await supabase.rpc('update_user_presence', { p_is_online: true });
      } catch (error) {
        // Silently handle presence update errors
      }
    }, 120000); // 2 minutes

    return () => clearInterval(presenceInterval);
  }, [user]);

  // Check if user has accepted the messages disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'messages')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'messages')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'messages',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Internal function to perform message sending (after disclaimer check)
  const sendMessageInternal = async () => {
    if ((!message.trim() && !selectedProtocol) || !user || !selected) return;

    // Create the message (content can be empty if only protocol is attached)
    const { data: newMessage, error } = await supabase
      .from('messages')
      .insert({ 
        sender_id: user.id, 
        receiver_id: selected, 
        content: message.trim() || 'Protocol shared', 
        reply_to_message_id: replyToMessage?.id || null 
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error creating message:', error);
      return;
    }

    // If a protocol is attached, create the attachment records
    if (selectedProtocol && newMessage?.id) {
      try {
        // Create message_attachment record
        const { data: attachmentData, error: attachmentError } = await supabase
          .from('message_attachments')
          .insert({
            message_id: newMessage.id,
            type: 'protocols',
            sort_order: 0
          })
          .select('id')
          .single();

        if (attachmentError) {
          console.error('Error creating message attachment:', attachmentError);
        } else if (attachmentData?.id) {
          // Create message_attachment_protocols record
          // First check if it already exists to avoid any ON CONFLICT issues
          const { data: existing } = await supabase
            .from('message_attachment_protocols')
            .select('id')
            .eq('message_attachment_id', attachmentData.id)
            .eq('protocol_id', selectedProtocol.id)
            .maybeSingle();

          if (!existing) {
            // Only insert if it doesn't already exist
            const { error: protocolError } = await supabase
              .from('message_attachment_protocols')
              .insert({
                message_attachment_id: attachmentData.id,
                protocol_id: selectedProtocol.id,
                sort_order: 0
              });

            if (protocolError) {
              // Log error but don't block the message send
              console.error('Error creating protocol attachment:', protocolError);
            }
          }
          // If it already exists, silently skip (duplicate prevention)
        }
      } catch (error) {
        console.error('Error attaching protocol:', error);
      }
    }

    // Notification is automatically created by database trigger create_message_notification_trigger
    // No need to call NotificationService.notifyMessage() here

    setMessage('');
    setReplyToMessage(null);
    setSelectedProtocol(null);
    fetchThread();
    fetchConversations();
  };

  const handleSend = async () => {
    if ((!message.trim() && !selectedProtocol) || !user || !selected) return;

    // Check if user has accepted the disclaimer
    const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(user.id);
    
    if (!hasAcceptedDisclaimer) {
      // Fetch disclaimer text and show modal
      const disclaimer = await fetchDisclaimerText();
      if (disclaimer && disclaimer.text) {
        setDisclaimerText(disclaimer.text);
        setDisclaimerVersion(disclaimer.version || 'v1.0');
        setShowDisclaimerModal(true);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        return;
      }
    }

    // User has accepted disclaimer, proceed with sending
    await sendMessageInternal();
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }

    try {
      if (!user) {
        setShowDisclaimerModal(false);
        return;
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(user.id, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // User has accepted disclaimer, proceed with sending
        await sendMessageInternal();
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
      }
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
  };

  const insertEmoji = (emoji) => {
    const input = inputRef.current;
    if (!input) return;

    const start = input.selectionStart;
    const end = input.selectionEnd;
    const newValue = message.slice(0, start) + emoji.native + message.slice(end);

    setMessage(newValue);
    setShowEmojiPicker(false);

    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + emoji.native.length, start + emoji.native.length);
    }, 0);
  };

  const handleReaction = async (messageId, emoji) => {
    if (!user) return;

    try {
      const reactions = messageReactions[messageId] || [];
      const existingReaction = reactions.find(r => r.user_id === user.id && r.emoji === emoji);

      if (existingReaction) {
        // Remove reaction
        const { error: deleteError } = await supabase
          .from('message_reactions')
          .delete()
          .eq('id', existingReaction.id);
        
        if (deleteError) {
          return;
        }
      } else {
        // Add reaction
        const { error: insertError } = await supabase
          .from('message_reactions')
          .insert({
            message_id: messageId,
            user_id: user.id,
            emoji: emoji
          });
        
        if (insertError) {
          return;
        }
      }

      fetchThread();
    } catch (error) {
      // Silently handle reaction errors
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    if (!user || !newContent.trim()) return;

    const { error } = await supabase
      .from('messages')
      .update({ content: newContent.trim() })
      .eq('id', messageId)
      .eq('sender_id', user.id);

    if (!error) {
      fetchThread();
      fetchConversations();
    }
  };

  const markMessagesAsRead = async (userId, senderId) => {
    try {
      // Call the database function to mark messages as read
      await supabase.rpc('mark_messages_as_read', {
        p_user_id: userId,
        p_sender_id: senderId
      });
    } catch (error) {
      // Silently handle read marking errors
    }
  };

  // Combine conversations and all users for unified search
  const conversationsWithSelected = selected && !conversations.find(c => c.id === selected)
    ? [{
        id: selected,
        username: allUsers.find(u => u.user_id === selected)?.username || 'Unknown User',
        avatar: allUsers.find(u => u.user_id === selected)?.profile_picture_url,
        lastMessage: 'Start a conversation...',
        lastMessageAt: new Date().toISOString(),
        messageId: null,
        isTemporary: true
      }, ...conversations]
    : conversations;

  const filteredConversations = search
    ? (() => {
        const searchLower = search.toLowerCase();
        
        // Filter existing conversations
        const matchedConvos = conversationsWithSelected.filter(c => 
          c.username.toLowerCase().includes(searchLower)
        );
        
        // Filter all users not in conversations (including the currently selected one)
        const conversationIds = new Set([
          ...conversations.map(c => c.id),
          ...(selected ? [selected] : [])
        ]);
        const matchedNewUsers = allUsers
          .filter(u => !conversationIds.has(u.user_id) && u.username?.toLowerCase().includes(searchLower))
          .map(u => ({
            id: u.user_id,
            username: u.username || 'Unknown',
            avatar: u.profile_picture_url,
            lastMessage: 'Start a conversation...',
            lastMessageAt: new Date().toISOString(),
            isTemporary: true,
            isNewContact: true
          }));
        
        return [...matchedConvos, ...matchedNewUsers];
      })()
    : conversationsWithSelected;

  const selectedProfile = conversationsWithSelected.find(c => c.id === selected);

  return (
    <div className="messages-container">
      <TopNavigation activePage="messages" />
      
      <div className="messages-layout">
        {(!isMobile || !selected) && (
          <MessagesSidebar
            search={search}
            onSearchChange={setSearch}
            conversations={filteredConversations}
            selectedId={selected}
            onSelectConversation={setSelected}
            loading={loading}
          />
        )}

        {(!isMobile || selected) && (
          <MessagesChatThread
            selectedProfile={selectedProfile}
            thread={thread}
            loadingThread={loadingThread}
            user={user}
            userProfiles={userProfiles}
            message={message}
            onMessageChange={setMessage}
            onSendMessage={handleSend}
            replyToMessage={replyToMessage}
            onSetReplyToMessage={setReplyToMessage}
            showEmojiPicker={showEmojiPicker}
            onToggleEmojiPicker={() => setShowEmojiPicker(!showEmojiPicker)}
            onInsertEmoji={insertEmoji}
            inputRef={inputRef}
            onBack={() => setSelected(null)}
            isMobile={isMobile}
            onReportMessage={(msg) => {
                                          setSelectedMessageForReport(msg);
                                          setShowReportModal(true);
                                        }}
            messageReactions={messageReactions}
            onReaction={handleReaction}
            editingMessage={editingMessage}
            onSetEditingMessage={setEditingMessage}
            onEditMessage={handleEditMessage}
            userPresence={userPresence}
            selectedProtocol={selectedProtocol}
            onSelectProtocol={setSelectedProtocol}
            onOpenProtocolSelector={() => setShowProtocolSelector(true)}
          />
        )}

        {showProtocolSelector && (
          <ProtocolSelector
            onSelect={(protocol) => {
              setSelectedProtocol(protocol);
              setShowProtocolSelector(false);
            }}
            onClose={() => setShowProtocolSelector(false)}
            selectedProtocol={selectedProtocol}
          />
        )}
      </div>

      {showReportModal && selectedMessageForReport && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setSelectedMessageForReport(null);
          }}
          message={selectedMessageForReport}
          reportedUserId={selectedMessageForReport.sender_id}
          reportedUsername={userProfiles[selectedMessageForReport.sender_id]?.username || 'User'}
        />
      )}

      {/* Messages Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h3>Messages Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for sending this message</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <BottomNavigation />
    </div>
  );
};

export default Messages; 
